import { useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { publicationColor } from '../../lib/format'
import { presetRange, todayEastern, type DateRange, type Preset } from '../../lib/visitorsRange'

type Props = {
  pub?: string
  title: string
  crumbs: { label: string; to?: string }[]
  range: DateRange
  onRange: (r: DateRange) => void
  right?: ReactNode
}

const PRESETS: { key: Exclude<Preset, 'custom'>; label: string }[] = [
  { key: 'today', label: 'Today' },
  { key: 'yesterday', label: 'Yesterday' },
  { key: '7d', label: '7 days' },
  { key: '30d', label: '30 days' },
  { key: '90d', label: '90 days' },
  { key: '12m', label: '12 months' },
]

const pill = (on: boolean) =>
  `min-h-[32px] px-3 font-mono text-[12px] border ${
    on ? 'border-amber-glow text-amber-glow bg-ink-700' : 'border-ink-500 text-paper-dim hover:text-paper hover:bg-ink-700'
  }`

export function VisitorsHeader({ pub, title, crumbs, range, onRange, right }: Props) {
  const [from, setFrom] = useState(range.from)
  const [to, setTo] = useState(range.to)
  const [showCustom, setShowCustom] = useState(range.preset === 'custom')
  const custom = showCustom || range.preset === 'custom'
  const canApply = from !== '' && to !== '' && from <= to

  return (
    <header className="border-b border-ink-500 pb-6 space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-6">
        <div>
          <nav aria-label="Breadcrumb" className="label mb-1 flex flex-wrap gap-2">
            {crumbs.map((c, i) => (
              <span key={`${c.label}-${i}`} className="flex gap-2">
                {i > 0 && <span aria-hidden="true">›</span>}
                {c.to ? <Link to={c.to} className="hover:text-paper">{c.label}</Link> : <span>{c.label}</span>}
              </span>
            ))}
          </nav>
          <h1 className="font-serif text-[42px] leading-[1.05] tracking-tight text-paper flex items-center gap-3">
            {pub && <span className="inline-block w-3 h-3" style={{ backgroundColor: publicationColor(pub) }} aria-hidden="true" />}
            {title}
          </h1>
        </div>
        {right && <div className="flex flex-col items-end gap-1 font-mono text-[12px] text-paper-dim">{right}</div>}
      </div>
      <div className="flex flex-wrap items-end gap-2">
        {PRESETS.map((p) => (
          <button
            key={p.key}
            type="button"
            className={pill(range.preset === p.key)}
            onClick={() => {
              setShowCustom(false)
              onRange(presetRange(p.key, todayEastern()))
            }}
          >
            {p.label}
          </button>
        ))}
        <button type="button" className={pill(custom)} onClick={() => {
            setFrom(range.from)
            setTo(range.to)
            setShowCustom(true)
          }}>
          Custom
        </button>
        {custom && (
          <div className="flex flex-wrap items-end gap-2 ml-2">
            <label className="flex flex-col gap-1">
              <span className="label">From</span>
              <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="min-h-[32px] bg-ink-900 border border-ink-500 px-2 font-mono text-[12px] text-paper" />
            </label>
            <label className="flex flex-col gap-1">
              <span className="label">To</span>
              <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="min-h-[32px] bg-ink-900 border border-ink-500 px-2 font-mono text-[12px] text-paper" />
            </label>
            <button
              type="button"
              disabled={!canApply}
              className={`${pill(false)} disabled:opacity-40`}
              onClick={() => onRange({ from, to, preset: 'custom' })}
            >
              Apply
            </button>
          </div>
        )}
        <span className="label text-paper-faint ml-2 self-center">vs previous period</span>
      </div>
    </header>
  )
}
